import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { RdPopover } from "../../redesign/table/RdPopover";
import { LinkSelectionDialog } from "../detail/LinkRow";
import {
    kontaktLabel,
    risikoLabel,
    vertragLabel,
    type CascadeResult,
    type LinkSlots,
} from "../detail/linkSlots";
import { kontakteGewaehlt, risikenGewaehlt, vertraegeGewaehlt } from "./bezugAuswahl";
import "./vorgangV3.css";

/**
 * Kontakt · Risiko · Vertrag eines To-dos als drei Kreise „K", „R", „V"
 * (PO 07.09.2026).
 *
 * Vorher standen an dieser Stelle die Kontaktverantwortlichkeit und die
 * Zuweisung als Chip-Reihe. Das ist die richtige Darstellung für den VORGANG,
 * wo die Bezüge das Thema sind — am einzelnen To-do fraßen sie die Zeile auf,
 * in der eigentlich Betreff, Fristen und Beteiligte stehen sollen, und der
 * Kunde wiederholte meist nur den des Vorgangs.
 *
 * Der Kreis sagt auf einen Blick, OB ein Bezug hängt (grün) oder nicht (grau
 * gestrichelt); WAS hängt, zeigt der Klick. So kostet die Angabe drei Kreise
 * statt einer halben Zeile, bleibt aber vollständig erreichbar — auch das
 * Hinzufügen, das vorher hinter einem gemeinsamen „+" mit Zwischenmenü lag.
 *
 * Der Buchstabe allein trägt die Bedeutung nicht: `title` und `aria-label`
 * nennen Sorte und Inhalt im Klartext, und die Farbe ist nie das einzige
 * Unterscheidungsmerkmal (gefüllt/grün gegen gestrichelt/grau).
 */
type Sorte = "kontakt" | "risiko" | "vertrag";

const BUCHSTABE: Record<Sorte, string> = { kontakt: "K", risiko: "R", vertrag: "V" };
const SORTE_LABEL: Record<Sorte, string> = { kontakt: "Kontakt", risiko: "Risiko", vertrag: "Vertrag" };

/** Ein Eintrag im Popover: Beschriftung + Ziel-Route des Datensatzes. */
type Eintrag = { id: number; label: string; pfad: string };

/** Was hängt in diesem Slot? Leeres Array = nichts zugewiesen. */
export function eintraegeFuer(slots: LinkSlots, sorte: Sorte): Eintrag[] {
    if (sorte === "kontakt") {
        const kontakte = slots.kontakte?.length ? slots.kontakte : slots.kontakt ? [slots.kontakt] : [];
        return kontakte.map((kontakt) => ({
            id: kontakt.id,
            label: kontaktLabel(kontakt),
            pfad: `/contacts/${kontakt.id}`,
        }));
    }
    if (sorte === "risiko") {
        return slots.risiken.map((r) => ({ id: r.id, label: risikoLabel(r), pfad: `/risks/${r.id}` }));
    }
    return slots.vertraege.map((v) => ({ id: v.id, label: vertragLabel(v), pfad: `/contracts/${v.id}` }));
}

type Props = {
    slots: LinkSlots;
    canEdit: boolean;
    /** Persistiert den Patch und übernimmt `result.slots` in den Seiten-State. */
    onApply: (result: CascadeResult) => void | Promise<void>;
    /**
     * Wessen Bezüge das sind — als Genitiv, der hinter die Sorte gehängt wird
     * („Risiko DES TO-DOS“). Seit dem 13.09.2026 stehen dieselben Kreise auch
     * am Vorgangskopf; dort wäre „des To-dos“ schlicht falsch. Ohne Angabe
     * bleibt es beim To-do, damit die vorhandenen Aufrufer unverändert bleiben.
     */
    gegenstand?: string;
};

export const BezugsKreise: React.FC<Props> = ({ slots, canEdit, onApply, gegenstand = "des To-dos" }) => {
    const navigate = useNavigate();
    const [dialog, setDialog] = useState<Sorte | null>(null);

    const apply = (result: CascadeResult): void => {
        // Leerer Patch = unveränderte Auswahl → kein PUT.
        if (!Object.keys(result.patch).length) return;
        void onApply(result);
    };

    const kreis = (sorte: Sorte) => {
        const eintraege = eintraegeFuer(slots, sorte);
        const belegt = eintraege.length > 0;
        const inhalt = belegt ? eintraege.map((e) => e.label).join(", ") : "nichts zugewiesen";
        const name = `${SORTE_LABEL[sorte]}: ${inhalt}`;

        return (
            <RdPopover
                key={sorte}
                width={260}
                dialogLabel={`${SORTE_LABEL[sorte]} ${gegenstand}`}
                align="end"
                trigger={({ toggle, ref }) => (
                    <button
                        ref={ref}
                        type="button"
                        className={`vg3-bezug${belegt ? " belegt" : ""}`}
                        title={name}
                        aria-label={name}
                        onClick={toggle}
                    >
                        {BUCHSTABE[sorte]}
                        {/* Mehrfach belegt? Dann sagt eine Hochzahl, WIE VIELE
                            hängen (PO 13.09.2026) — sonst sähe ein Kreis mit
                            fünf Risiken aus wie einer mit einem. Sie ist reine
                            Sichtbarkeit und `aria-hidden`: der Name des Knopfs
                            zählt die Einträge schon im Klartext auf, ein
                            zweites Mal die Zahl wäre Doppelung. Bei genau einem
                            Bezug bleibt es bewusst nur beim Buchstaben. */}
                        {eintraege.length > 1 && <sup aria-hidden>{eintraege.length}</sup>}
                    </button>
                )}
            >
                {(close) => (
                    // Portal an `body` — außerhalb des `.kvp-rd`-Wrappers der
                    // Seite; ohne eigene Hülle griffen weder Regeln noch Tokens.
                    <div className="kvp-rd vg3-menu">
                        <div className="vg3-bezug-kopf">{SORTE_LABEL[sorte]}</div>
                        {eintraege.length === 0 ? (
                            <div className="vg3-bezug-leer">Nichts zugewiesen.</div>
                        ) : (
                            eintraege.map((e) => (
                                <button
                                    key={e.id}
                                    type="button"
                                    className="vg3-menu-item"
                                    title={`${e.label} öffnen`}
                                    onClick={() => {
                                        close();
                                        navigate(e.pfad);
                                    }}
                                >
                                    {e.label}
                                </button>
                            ))
                        )}
                        {canEdit && (
                            <button
                                type="button"
                                className="vg3-menu-item vg3-bezug-aktion"
                                onClick={() => {
                                    close();
                                    setDialog(sorte);
                                }}
                            >
                                {belegt ? "ändern" : "hinzufügen"}
                            </button>
                        )}
                    </div>
                )}
            </RdPopover>
        );
    };

    return (
        <>
            <span className="vg3-bezuege" role="group" aria-label={`Kontakt, Risiko und Vertrag ${gegenstand}`}>
                {(["kontakt", "risiko", "vertrag"] as Sorte[]).map(kreis)}
            </span>

            {dialog && (
                <LinkSelectionDialog
                    kind={dialog}
                    slots={slots}
                    onClose={() => setDialog(null)}
                    onKontakt={(k) => {
                        setDialog(null);
                        apply(kontakteGewaehlt(slots, [k]));
                    }}
                    onKontakte={(kontakte) => {
                        setDialog(null);
                        apply(kontakteGewaehlt(slots, kontakte));
                    }}
                    onRisiken={(r) => {
                        setDialog(null);
                        void risikenGewaehlt(slots, r).then(apply);
                    }}
                    onVertraege={(v) => {
                        setDialog(null);
                        void vertraegeGewaehlt(slots, v).then(apply);
                    }}
                />
            )}
        </>
    );
};
